import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useAccount, useMsal } from "@azure/msal-react";
import type { AccountInfo } from "@azure/msal-browser";
import { setUserInfo, clearUserInfo } from "../UserAuthAndAdminSlice_Store";
import { silentRequest } from "../config/authConfig";
import type { RootState, AppDispatch } from "../types/redux";

interface UseAuthIntegrationReturn {
  account: AccountInfo | null;
  isAuthenticated: boolean;
}

export const useAuthIntegration = (): UseAuthIntegrationReturn => {
  const dispatch = useDispatch<AppDispatch>();
  const account = useAccount();
  const { instance, accounts } = useMsal();
  const isReduxAuthenticated = useSelector((state: RootState) => state.userAuthAndAdminSlice.isAuthenticated);

  useEffect(() => {
    // Use the specific account from useAccount, or fall back to first available account
    const activeAccount = account || (accounts.length > 0 ? accounts[0] : null);

    // Don't overwrite custom auth - only process MSAL accounts if no Redux auth exists
    if (activeAccount && accounts.length > 0 && !isReduxAuthenticated) {
      const getTokenAndSetUser = async (): Promise<void> => {
        try {
          const response = await instance.acquireTokenSilent({
            ...silentRequest,
            account: activeAccount,
          });

          dispatch(setUserInfo({
            account: activeAccount,
            accessToken: response.accessToken,
          }));
        } catch (error) {
          // Still set user info even without token since user is authenticated with MSAL
          dispatch(setUserInfo({
            account: activeAccount,
            accessToken: null,
          }));
        }
      };

      void getTokenAndSetUser();
    } else if (accounts.length === 0 && !isReduxAuthenticated) {
      // Only clear user info if no MSAL accounts and no Redux authentication
      // This allows custom auth (username/password) to persist without MSAL
      dispatch(clearUserInfo());
    }
    // If there are no MSAL accounts but Redux shows authentication,
    // it means we have custom auth from localStorage - leave it as is
  }, [account, accounts, instance, dispatch, isReduxAuthenticated]);

  return {
    account,
    isAuthenticated: !!account,
  };
};
